/**
 * Eventos PostHog del cupo fiscal extra en el modal de pago del plan (ADM-740).
 * Fuente única de los nombres: el spec fija que son exactamente estos. Son distintos de los
 * `fiscal_offer_*` del experimento de signup para no mezclar su embudo.
 * - `viewed`: el selector de cantidad se vio editable con el modal abierto (una vez por apertura).
 * - `selected`: entró al Plan Fiscal o cambió la cantidad dentro de fiscal.
 * - `removed`: salió del Plan Fiscal.
 * - `blocked`: el servidor rechazó el cobro por el cupo fiscal (cuatro 409 o el 400 de cantidad).
 */
export const FISCAL_OFFER_CHECKOUT_EVENTS = {
    viewed: 'fiscal_offer_checkout_viewed',
    selected: 'fiscal_offer_checkout_selected',
    removed: 'fiscal_offer_checkout_removed',
    blocked: 'fiscal_offer_checkout_blocked',
} as const;

export type FiscalOfferCheckoutChannel = 'pago_movil' | 'card' | 'debito_inmediato';

export interface FiscalOfferCheckoutInput {
    currentPlanFiscal: boolean;
    targetPlanFiscal: boolean;
    /** Cantidad de facturas adicionales pedida en este evento. */
    extraControlNumbers: number;
    /** Cantidad de la que se parte: la anterior en `selected`/`removed`, la facturada en el resto. */
    previousExtraControlNumbers: number | null;
    paymentFrequency: string | null;
    fiscalSalesAvailable: boolean;
    /** Solo en `blocked`. */
    errorCode?: string | null;
    /** Solo en `blocked`. */
    channel?: FiscalOfferCheckoutChannel | null;
}

export interface FiscalOfferCheckoutProps {
    current_plan_fiscal: boolean;
    target_plan_fiscal: boolean;
    extra_control_numbers: number;
    previous_extra_control_numbers: number | null;
    payment_frequency: string | null;
    fiscal_sales_available: boolean;
    error_code: string | null;
    channel: FiscalOfferCheckoutChannel | null;
}

const quantityOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined) {
        return null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null;
};

/**
 * Props de los eventos del cupo fiscal. Siempre devuelve las mismas claves (lo que falta
 * viaja `null`), sin PII ni ids: los breakdowns de PostHog no dependen del punto de captura.
 */
export function buildFiscalOfferCheckoutProps(input: FiscalOfferCheckoutInput): FiscalOfferCheckoutProps {
    return {
        current_plan_fiscal: input.currentPlanFiscal === true,
        target_plan_fiscal: input.targetPlanFiscal === true,
        extra_control_numbers: quantityOrNull(input.extraControlNumbers) ?? 0,
        previous_extra_control_numbers: quantityOrNull(input.previousExtraControlNumbers),
        payment_frequency: input.paymentFrequency ? String(input.paymentFrequency).trim().toLowerCase() : null,
        fiscal_sales_available: input.fiscalSalesAvailable === true,
        error_code: input.errorCode ? String(input.errorCode) : null,
        channel: input.channel ?? null,
    };
}
